"use client";

import { useEffect, useSyncExternalStore } from "react";
import { motion } from "framer-motion";
import { PartyPopper, X } from "lucide-react";
import type { MissionData } from "@/types/mission";

/**
 * Earnings celebration gate — fires ONLY when earnings.json carries verified
 * entries beyond what this viewer has already seen (marker in localStorage).
 *
 * Honest by construction: with 0 verified entries (today's truth) this
 * component renders absolutely nothing. It can never celebrate a handshake,
 * a probe, or any state other than a real verified earnings entry.
 *
 * The "seen" marker is read through useSyncExternalStore (localStorage as an
 * external store) — no setState-in-effect, hydration-safe, and a same-tab
 * dismiss re-reads the store immediately.
 */

const SEEN_KEY = "aces-celebration-seen";

const subscribe = (onChange: () => void) => {
  window.addEventListener("storage", onChange); // cross-tab updates
  window.addEventListener("aces-celebration-seen", onChange); // same-tab dismiss
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener("aces-celebration-seen", onChange);
  };
};

const getSnapshot = (): string => {
  try {
    return window.localStorage.getItem(SEEN_KEY) ?? "0";
  } catch {
    return "0"; // storage unavailable (private mode) — treat as unseen
  }
};

const getServerSnapshot = (): string => "0";

export function CelebrationGate({ data }: { data: MissionData }) {
  const verified = data.earnings.filter((e) => e.status === "verified");
  const verifiedCount = verified.length;
  const seen = Number(useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)) || 0;

  /* keep the marker honest at zero while there is nothing to celebrate */
  useEffect(() => {
    if (verifiedCount === 0 && seen !== 0) {
      try {
        window.localStorage.setItem(SEEN_KEY, "0");
        window.dispatchEvent(new Event("aces-celebration-seen"));
      } catch {
        /* storage unavailable — no-op */
      }
    }
  }, [verifiedCount, seen]);

  const newCount = verifiedCount - seen;
  const show = verifiedCount > 0 && newCount > 0;

  if (!show) return null;

  const first = seen === 0; // this viewer has seen zero before
  const latest = verified[0];

  const dismiss = () => {
    try {
      window.localStorage.setItem(SEEN_KEY, String(verifiedCount));
      window.dispatchEvent(new Event("aces-celebration-seen"));
    } catch {
      /* ignore — next render still computes from the same data */
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      role="status"
      aria-live="polite"
      className="relative overflow-hidden rounded-lg border border-emerald-500/50 bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-amber-500/10 px-4 py-3"
    >
      {/* soft radiating pulse — decorative only, honors prefers-reduced-motion via CSS kill-switch */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 animate-[celebrate-pulse_2.4s_ease-out_infinite] bg-[radial-gradient(ellipse_at_left,rgba(16,185,129,0.12),transparent_60%)]"
      />
      <div className="relative flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/40 bg-emerald-500/15">
          <PartyPopper className="size-4.5 text-emerald-300" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-sm font-bold tracking-wide text-emerald-200">
            {first ? "FIRST VERIFIED SETTLEMENT" : `${newCount} NEW VERIFIED ${newCount === 1 ? "ENTRY" : "ENTRIES"}`}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-emerald-100/80">
            {first
              ? "The mission's first real, evidence-verified earning has landed in earnings.json — this celebration fires only on real verified entries, never on handshakes or probes."
              : `Verified entries now: ${verifiedCount}. Latest: ${latest.amount} ${latest.asset} from ${latest.source_opportunity}.`}
            {latest.txid_or_link && (
              <>
                {" "}
                <a
                  href={latest.txid_or_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-emerald-300 underline decoration-emerald-500/40 underline-offset-2"
                >
                  view evidence
                </a>
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss celebration"
          className="shrink-0 rounded-md p-1.5 text-emerald-300/70 transition-colors hover:bg-emerald-500/10 hover:text-emerald-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-400/60"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </motion.section>
  );
}
